import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, FileText, FileSpreadsheet, RefreshCw, Folder, Check, X, Eye, Download, ChevronUp, Copy, Info, Search, Table2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PDFViewer from "@/components/PDFViewer";
import FloatingWindow from "@/components/FloatingWindow";
import { copyToClipboard, topFrameDownload, parseCSV, parseExcel } from "@/lib/downloadUtils";

import { API_URL } from "@/lib/api";
const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const ReportIcon = ({ format }) => {
  if (format === "pdf") return <FileText className="w-4 h-4 text-rose-500" />;
  if (format === "xlsx") return <Table2 className="w-4 h-4 text-sky-600" />;
  return <FileSpreadsheet className="w-4 h-4 text-emerald-500" />;
};
const getIconBg = (f) => f === "pdf" ? "bg-rose-50" : f === "xlsx" ? "bg-sky-50" : "bg-emerald-50";

const LocalReportsExplorer = ({ open, onOpenChange, localId, localName }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [downloadPanelId, setDownloadPanelId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/local/${localId}/reports/list`); setReports(r.data.reports); }
    catch { toast.error("Error al cargar los reportes"); }
    finally { setLoading(false); }
  }, [localId]);

  const closeViewer = useCallback(() => {
    setViewer(prev => { if (prev?.blobUrl) window.URL.revokeObjectURL(prev.blobUrl); return null; });
  }, []);

  useEffect(() => {
    if (open) { fetchReports(); } else { closeViewer(); setDownloadPanelId(null); setSearchTerm(""); setTypeFilter("all"); }
  }, [open, fetchReports, closeViewer]);

  const filtered = useMemo(() => reports.filter(r => {
    if (typeFilter !== "all" && r.format !== typeFilter) return false;
    if (searchTerm && !r.filename.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  }), [reports, searchTerm, typeFilter]);

  const handleView = async (report) => {
    if (viewer?.filename === report.filename) { closeViewer(); return; }
    closeViewer(); setDownloadPanelId(null);
    setBusyId(report.filename + "_view");
    try {
      const res = await axios.get(`${API}/local/${localId}/reports/download/${report.filename}`, { responseType: "blob" });
      if (report.format === "pdf") {
        const blob = new Blob([res.data], { type: "application/pdf" });
        setViewer({ filename: report.filename, type: "pdf", blobUrl: window.URL.createObjectURL(blob) });
      } else if (report.format === "xlsx") {
        setViewer({ filename: report.filename, type: "xlsx", csvRows: await parseExcel(res.data) });
      } else {
        setViewer({ filename: report.filename, type: "csv", csvRows: parseCSV(await res.data.text()) });
      }
    } catch { toast.error("Error al cargar el archivo"); }
    finally { setBusyId(null); }
  };

  const handleDelete = async (filename) => {
    try {
      await axios.delete(`${API}/local/${localId}/reports/delete/${filename}`);
      toast.success(`Eliminado: ${filename}`);
      if (viewer?.filename === filename) closeViewer();
      if (downloadPanelId === filename) setDownloadPanelId(null);
      setConfirmDeleteId(null);
      fetchReports();
    } catch { toast.error("Error al eliminar"); setConfirmDeleteId(null); }
  };

  const fmtSize = (b) => { if (!b) return "0 B"; const k=1024, s=["B","KB","MB"], i=Math.floor(Math.log(b)/Math.log(k)); return parseFloat((b/Math.pow(k,i)).toFixed(1))+" "+s[i]; };
  const fmtDate = (iso) => new Date(iso).toLocaleString("es-BO", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit", hour12:false, timeZone:"America/La_Paz" });

  return (
    <FloatingWindow open={open} onClose={() => onOpenChange(false)} title={`${localName} - Reportes`}>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm" data-testid="local-reports-search" />
        </div>
        <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden h-8">
          {["all","pdf","xlsx","csv"].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 h-full text-xs font-medium transition-colors ${typeFilter===t ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {t === "all" ? "Todos" : t.toUpperCase()}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{filtered.length} de {reports.length}</span>
        <Button variant="outline" size="sm" onClick={fetchReports} disabled={loading} className="h-8">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><RefreshCw className="w-7 h-7 animate-spin mx-auto text-sky-500 mb-2" /><p className="text-sm text-slate-500">Cargando...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center"><Folder className="w-10 h-10 mx-auto text-slate-300 mb-2" /><p className="text-sm text-slate-500">{reports.length===0 ? "No hay reportes generados aun" : "Sin coincidencias"}</p></div>
        ) : (
          <div className="divide-y" style={{ maxHeight: 300, overflowY: "auto" }}>
            {filtered.map((report, i) => {
              const isViewing = viewer?.filename === report.filename;
              const isDlOpen = downloadPanelId === report.filename;
              return (
                <div key={report.filename} data-testid={`local-report-item-${i}`}>
                  <div className={`px-4 py-2.5 flex items-center justify-between transition-colors ${isViewing||isDlOpen ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-1.5 rounded-md shrink-0 ${getIconBg(report.format)}`}><ReportIcon format={report.format} /></div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{report.filename}</p>
                        <p className="text-xs text-slate-400">{fmtDate(report.created_at)} - {fmtSize(report.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {confirmDeleteId === report.filename ? (
                        <div className="flex items-center gap-1 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1">
                          <span className="text-xs text-rose-700">Eliminar?</span>
                          <button onClick={() => handleDelete(report.filename)} className="p-1 text-rose-600 hover:bg-rose-100 rounded"><Check className="w-3 h-3" /></button>
                          <button onClick={() => setConfirmDeleteId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => { setDownloadPanelId(null); handleView(report); }}
                            disabled={busyId!==null} className={`px-2 text-sky-600 hover:bg-sky-50 ${isViewing?"bg-sky-100":""}`}
                            data-testid={`local-view-report-${i}`}>
                            {busyId===report.filename+"_view" ? <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /> : isViewing ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button variant="outline" size="sm"
                            onClick={() => { closeViewer(); setDownloadPanelId(isDlOpen?null:report.filename); }}
                            disabled={busyId!==null}
                            className={`px-2 text-emerald-700 border-emerald-400 hover:bg-emerald-50 ${isDlOpen?"bg-emerald-50":""}`}
                            data-testid={`local-download-report-${i}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(report.filename)}
                            disabled={busyId!==null} className="px-2 text-rose-500 hover:bg-rose-50"
                            data-testid={`local-delete-report-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {isDlOpen && (
                    <div className="px-4 pb-4 pt-1 bg-sky-50 border-t border-sky-100">
                      <div className="border border-sky-200 rounded-lg bg-sky-50 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Download className="w-4 h-4 text-sky-600" />Guardar archivo</span>
                          <button onClick={() => setDownloadPanelId(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="flex gap-2 items-center">
                          <input readOnly value={`${BACKEND_URL}/api/local/${localId}/reports/download/${report.filename}`}
                            onClick={e => e.target.select()} className="flex-1 text-xs font-mono bg-white border rounded px-2 py-1.5 text-slate-600" />
                          <Button size="sm" onClick={async () => {
                            const ok = await copyToClipboard(`${BACKEND_URL}/api/local/${localId}/reports/download/${report.filename}`);
                            if (ok) toast.success("URL copiada"); else toast.info("Selecciona y copia con Ctrl+C");
                          }} className="bg-sky-600 hover:bg-sky-700 text-white h-8 px-3 text-xs shrink-0">
                            <Copy className="w-3 h-3 mr-1" />Copiar
                          </Button>
                        </div>
                        <Button size="sm" onClick={() => { topFrameDownload(report.filename, `${BACKEND_URL}/api/local/${localId}/reports/download/${report.filename}`); toast.success(`Descargando: ${report.filename}`); }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs">
                          <Download className="w-3 h-3 mr-1" />Descargar ahora
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewer && (
        <div className="border rounded-lg overflow-hidden flex flex-col" style={{ minHeight: 280 }} data-testid="local-inline-viewer">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b shrink-0">
            <div className="flex items-center gap-2">
              <ReportIcon format={viewer.type} />
              <span className="text-sm font-medium text-slate-700 truncate max-w-sm">{viewer.filename}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={closeViewer} className="h-7 w-7 p-0"><X className="w-4 h-4" /></Button>
          </div>
          {viewer.type === "pdf" && (
            <div className="overflow-auto" style={{ maxHeight: 340 }}><PDFViewer blobUrl={viewer.blobUrl} /></div>
          )}
          {(viewer.type === "csv" || viewer.type === "xlsx") && (
            <div className="overflow-auto" style={{ maxHeight: 340 }}>
              {viewer.csvRows?.length > 0 ? (
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr>{viewer.csvRows[0]?.map((h,i) => <th key={i} className="px-3 py-2 text-left font-semibold text-slate-700 bg-slate-100 border-b border-r border-slate-200 whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {viewer.csvRows.slice(1,101).map((row,ri) => (
                      <tr key={ri} className={ri%2===0?"bg-white":"bg-slate-50"}>
                        {row.map((cell,ci) => <td key={ci} className="px-3 py-1.5 border-b border-r border-slate-100 whitespace-nowrap max-w-xs truncate">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="p-4 text-sm text-slate-500">No se pudo leer el archivo.</p>}
            </div>
          )}
        </div>
      )}
    </FloatingWindow>
  );
};

export default LocalReportsExplorer;
